import React, { useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Alert,
    CircularProgress
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useGetPromotionTriggerSettingsInfinite } from '../../../../client/hooks/products/trigger.queries';

export default function TriggerSettingsModal({ 
    open, 
    onClose, 
    selectedTriggerSettings 
}) {
    const tableContainerRef = useRef(null);
    
    // Hook para paginação infinita
    const {
        data: paginatedSettings,
        totalItems,
        isLoading,
        hasNextPage,
        loadNextPage,
        clearCache
    } = useGetPromotionTriggerSettingsInfinite(
        selectedTriggerSettings?.trigger?._id,
        open && selectedTriggerSettings?.trigger?._id
    );
    
    // Função para detectar scroll no final da tabela
    const handleScroll = (event) => {
        const { scrollTop, scrollHeight, clientHeight } = event.target;
        const threshold = 100; // 100px antes do final
        
        if (scrollHeight - scrollTop - clientHeight <= threshold && hasNextPage && !isLoading) {
            loadNextPage();
        }
    };
    
    // Limpar cache ao fechar o modal
    useEffect(() => {
        if (!open) {
            clearCache();
        }
    }, [open, clearCache]);
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { 
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(255, 152, 0, 0.15)'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: '1px solid #e0e0e0',
                pb: 3,
                pt: 3
            }}>
                <Box>
                    <Typography variant="h5" sx={{ 
                        fontWeight: 700,
                        color: '#ff9800',
                        mb: 0.5 
                    }}>
                        Settings do Trigger
                    </Typography>
                    {selectedTriggerSettings && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body1" sx={{ 
                                fontWeight: 600,
                                color: '#333'
                            }}>
                                {selectedTriggerSettings.trigger.promotion_type}
                            </Typography>
                            <Box sx={{
                                bgcolor: selectedTriggerSettings.trigger.active ? '#e8f5e8' : '#fff3e0',
                                color: selectedTriggerSettings.trigger.active ? '#2e7d32' : '#f57c00',
                                px: 2,
                                py: 0.5,
                                borderRadius: 2,
                                fontSize: '0.875rem',
                                fontWeight: 600
                            }}>
                                {selectedTriggerSettings.trigger.active ? 'Ativo' : 'Inativo'}
                            </Box>
                        </Box>
                    )}
                </Box>
                <IconButton 
                    onClick={onClose} 
                    sx={{ 
                        color: '#666',
                        '&:hover': {
                            bgcolor: 'rgba(255, 152, 0, 0.1)',
                            color: '#ff9800'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ p: 4 }}>
                {paginatedSettings?.length > 0 || totalItems > 0 ? (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {paginatedSettings?.length || 0} de {totalItems} settings carregados
                            </Typography>
                        </Box>
                        <TableContainer 
                        component={Paper}
                        ref={tableContainerRef}
                        onScroll={handleScroll}
                        sx={{ 
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: '1px solid #f0f0f0',
                            maxHeight: 400, // Altura fixa para permitir scroll
                            overflow: 'auto'
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#fafafa' }}>
                                    <TableCell sx={{ 
                                        fontWeight: 700,
                                        color: '#ff9800',
                                        fontSize: '0.95rem'
                                    }}>
                                        MLB Item
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 700,
                                        color: '#ff9800',
                                        fontSize: '0.95rem'
                                    }}>
                                        Preço Mínimo
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 700,
                                        color: '#ff9800',
                                        fontSize: '0.95rem'
                                    }}>
                                        Data de Criação
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedSettings?.map((setting, index) => (
                                    <TableRow 
                                        key={setting._id || `${setting.mlb_item}-${setting.trigger_id}`}
                                        sx={{
                                            '&:hover': { 
                                                bgcolor: 'rgba(255, 152, 0, 0.04)' 
                                            },
                                            '&:nth-of-type(even)': {
                                                bgcolor: '#fafafa'
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Typography sx={{ 
                                                fontFamily: 'monospace',
                                                fontSize: '0.9rem',
                                                fontWeight: 500,
                                                color: '#333'
                                            }}>
                                                {setting.mlb_item}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{
                                                bgcolor: '#e8f5e8',
                                                color: '#2e7d32',
                                                px: 2,
                                                py: 1,
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                fontSize: '0.9rem',
                                                display: 'inline-block'
                                            }}>
                                                R$ {setting.min_price?.toFixed(2) || '0.00'}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ 
                                                color: '#666',
                                                fontSize: '0.85rem'
                                            }}>
                                                {new Date(setting.createdAt).toLocaleDateString('pt-BR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                
                                {/* Loader para próxima página */}
                                {isLoading && (
                                    <TableRow>
                                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                <CircularProgress size={20} />
                                                <Typography variant="body2" color="text.secondary">
                                                    Carregando mais settings...
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                                
                                {/* Mensagem de final */}
                                {!hasNextPage && paginatedSettings?.length > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Todos os {totalItems} settings foram carregados
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        </TableContainer>
                    </>
                ) : (
                    <Alert 
                        severity="info" 
                        sx={{ 
                            textAlign: 'center',
                            borderRadius: 2,
                            border: '1px solid #e3f2fd',
                            bgcolor: '#f8f9ff'
                        }}
                    >
                        <Typography sx={{ fontWeight: 500, mb: 1 }}>
                            {isLoading ? 'Carregando settings...' : 'Este trigger não possui configurações específicas por produto.'}
                        </Typography>
                        {!isLoading && (
                            <Typography variant="body2" color="text.secondary">
                                {selectedTriggerSettings?.trigger?.apply_to_all_candidates 
                                    ? "Ele está configurado para aplicar a todos os candidatos."
                                    : "Configure produtos específicos na edição do trigger."
                                }
                            </Typography>
                        )}
                    </Alert>
                )}
            </DialogContent>
            
            <DialogActions sx={{ 
                p: 3, 
                borderTop: '1px solid #e0e0e0',
                justifyContent: 'flex-end'
            }}>
                <Button 
                    onClick={onClose}
                    variant="contained"
                    sx={{ 
                        textTransform: 'none',
                        bgcolor: '#ff9800',
                        color: 'white',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': { 
                            bgcolor: '#f57c00',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
                        },
                        transition: 'all 0.2s ease'
                    }}
                >
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
}